/* eslint-disable */
// BBOT — Phase D percentile report (player-facing). ISOLATED component so the wire-in touches the
// 15.7k-line App.js minimally. Renders the approved one-page Letter design: header (logo+identity+QR)
// → tagline → summary (plain restatement of backed percentiles) → percentile bar block (centerpiece)
// → tool detail → omissions → footer disclaimers. 100% data-driven from r.percentiles (Phase-C shape):
//   { bracket, version, metrics: { key: {percentile, display, mode, sample_n, label} } }
// GOVERNING PRINCIPLE = NON-REVOCABILITY: every line traces to a measured percentile. NO verdict, NO
// projection. Dormant until the backend PERCENTILE_MODE flag flips (no real report carries percentiles
// yet), so wiring it in changes nothing live.
import React, { useState, useEffect, useRef } from "react";

// Fixed report canvas height. The flex composition fills exactly one page to this height. Calibrated
// for the downloadReportPdf -> html2pdf RASTER path: that path clones the report into a 760px-wide wrap
// and html2canvas->jsPDF scales it to the Letter content width, so the px height that maps to one page
// is NOT a literal 11in. CALIBRATED VALUE set after running the html2pdf harness (see PercentilePdfTest).
const REPORT_H = 955; // px — calibrated one-page via the real html2pdf path (raster, 760px clone); kept a
                      // safety buffer below the ~1034px-wrap raster threshold (1068px→3 pages, 1030px→1 page)
const NAVY = "#2970b8", BLUEMID = "#3a66c0", CORAL = "#e8526a";
const INK = "#2970b8", INK2 = "#6a7da6", HAIR = "#dbe6fb";
// ESTIMATED (video) tier color — deliberately a muted slate, DISTINCT from radar blue/coral so an
// estimate can never read as a radar-Verified mark. (Video-velo framework; renders only with a band.)
const EST = "#8a93a3", ESTLINE = "#6b7585";
// blue dominant low, coral saturates LATE so a high mark pops coral at small scale
const TRACK_GRAD = "linear-gradient(90deg,#2970b8 0%,#5575c6 38%,#9a73ac 60%,#ec6f86 82%,#e8526a 100%)";

// key -> tool label + source bucket + unit + direction. radar = MPH velo (device overlay); measured =
// pose/CV or on-screen timer. dir: "higher"/"lower" = a clear better axis (ranked + leaderboard-eligible);
// "neutral" = a MEASURED tool with NO "better" (a style or optimal range — arm slot/stride/tempo/weight-
// shift/torso-tilt): it renders a DISTRIBUTION-POSITION bar (where a player falls vs peers) but is NOT
// betterness-ranked and NOT leaderboard-eligible (calling a 90th-pct arm slot "elite" would fabricate a
// quality claim). Order defines render order (grouped by tool family). Superseded 2026-07-12: see the ten-key META below.
// LOCKED 2026-07-12 (percentile-scope-locked-ten-metrics): percentiles = EXACTLY these ten radar/timed
// tools (1:1 with backend _PERCENTILE_ACTIVE). Every ML/pose metric is REPORT-ONLY (rendered as a measured
// result + rep spread elsewhere, never a rank) until BBOT's own accumulated data seeds their age curves.
// Throw velo is split by position (each ranks vs its own peer group); a player carries only his position's key.
const META = {
  exitVeloPeak:            {tool:"Power",    src:"radar",    unit:"mph", dir:"higher"},
  exitVeloAvg:             {tool:"Power avg",src:"radar",    unit:"mph", dir:"higher"},
  pitchVeloPeak:           {tool:"Velo pk",  src:"radar",    unit:"mph", dir:"higher"},
  pitchVeloAvg:            {tool:"Velo avg", src:"radar",    unit:"mph", dir:"higher"},
  of_throw_velo_top:       {tool:"OF Arm",   src:"radar",    unit:"mph", dir:"higher"},
  if_throw_velo_top:       {tool:"IF Arm",   src:"radar",    unit:"mph", dir:"higher"},
  catcher_throw_velo_top:  {tool:"C Arm",    src:"radar",    unit:"mph", dir:"higher"},
  dash60:                  {tool:"Run",      src:"measured", unit:"s",   dir:"lower"},
  h2f:                     {tool:"H-to-1B",  src:"measured", unit:"s",   dir:"lower"},
  popTime:                 {tool:"Pop time", src:"measured", unit:"s",   dir:"lower"},
};
const ORDER = ["exitVeloPeak","exitVeloAvg","pitchVeloPeak","pitchVeloAvg",
  "of_throw_velo_top","if_throw_velo_top","catcher_throw_velo_top","dash60","h2f","popTime"];

// the 9 admin-set age brackets (1:1 with the backend _AGE_BRACKETS) — for the coach-search dropdown
export const AGE_BRACKETS = ["13U","14U","15U","16U","17U","18U","Top College","College","Pro-MLB"];
// coach-friendly metric labels for the search dropdown (1:1 with the locked ten percentiled keys)
const SEARCH_LABEL = {
  exitVeloPeak:"Exit velo (peak)", exitVeloAvg:"Exit velo (avg)",
  pitchVeloPeak:"Fastball velo (peak)", pitchVeloAvg:"Fastball velo (avg)",
  of_throw_velo_top:"OF throw velo", if_throw_velo_top:"IF throw velo", catcher_throw_velo_top:"Catcher throw velo",
  dash60:"60-yard dash", h2f:"Home-to-first", popTime:"Pop time",
};
// rankable = has a clear better direction -> leaderboard-eligible. neutral tools are distribution-only.
export const PERCENTILE_METRICS = ORDER.map(function(k){ return {key:k, label:SEARCH_LABEL[k]||k, tool:META[k].tool, src:META[k].src, unit:META[k].unit, dir:META[k].dir, rankable:META[k].dir!=="neutral"}; });

function clampL(p){ return Math.max(4, Math.min(96, Number(p))); }
function srcLabel(s){ return s === "radar" ? "radar" : "measured"; }
function pctOfTen(p){ return Math.round(Number(p) / 10); } // "~9 of 10 peers"

function pctsOf(rep){
  if(!rep) return null;
  var p = rep.percentiles || (rep.fullReport && rep.fullReport.percentiles) || (rep.full_report && rep.full_report.percentiles);
  return (p && p.metrics) ? p : null;
}
function measuredOf(rep){
  return (rep && (rep.measuredValues || (rep.fullReport && rep.fullReport.measuredValues))) || {};
}
function fmtRaw(rep, key){
  var v = measuredOf(rep)[key];
  if(v === undefined || v === null || v === "") return null;
  var m = META[key]; if(!m) return String(v);
  return v + " " + m.unit + " (" + srcLabel(m.src) + ")";
}

// MAX per-metric percentile across this player's ADMIN-APPROVED prior reports (his ceiling). Excludes
// the current report. Returns {percentile, display, mode, sample_n, raw} or null (first-ever => null).
function bbotBestAcross(priorReports, playerId, key, currentId){
  var best = null;
  (priorReports || []).forEach(function(pr){
    if(String(pr.playerId || pr.player_id) !== String(playerId)) return;
    if(String(pr.id) === String(currentId)) return;
    if(!(pr.status === "approved" || pr.status === "published")) return;
    var pc = pctsOf(pr); if(!pc || !pc.metrics[key]) return;
    var mv = pc.metrics[key];
    if(mv.percentile == null) return;
    if(best == null || Number(mv.percentile) > Number(best.percentile)){
      best = {percentile:mv.percentile, display:mv.display, mode:mv.mode, sample_n:mv.sample_n, raw:fmtRaw(pr, key)};
    }
  });
  return best;
}

// PHASE E search primitive: a player's standing on ONE metric within a bracket, across their APPROVED
// reports. ceiling = max percentile (the search ranks on this — "his best"); session = latest report's
// value (so a result renders the SAME two-color best/this-session bar as the report). Returns null if the
// player has no approved percentile for that metric+bracket. Reads ONLY measured percentiles — never
// fabricates a standing for a player who wasn't measured on that tool.
export function searchPlayerMetric(reports, playerId, key, bracket){
  if(!META[key]) return null;
  var rs = (reports || []).filter(function(r){
    if(String(r.playerId || r.player_id) !== String(playerId)) return false;
    if(!(r.status === "approved" || r.status === "published")) return false;
    var pc = pctsOf(r); if(!pc || !pc.metrics[key] || pc.metrics[key].percentile == null) return false;
    if(bracket && pc.bracket !== bracket) return false;
    return true;
  });
  if(!rs.length) return null;
  var ceiling = null, latest = null;
  rs.forEach(function(r){
    var mv = pctsOf(r).metrics[key];
    if(!ceiling || Number(mv.percentile) > Number(ceiling.mv.percentile)) ceiling = {r:r, mv:mv};
    if(!latest || String(r.date || "") > String(latest.r.date || "")) latest = {r:r, mv:mv};
  });
  var cm = ceiling.mv, lm = latest.mv;
  return {
    key:key, tool:META[key].tool, pct:Number(cm.percentile), bracket:pctsOf(ceiling.r).bracket,
    best:{pct:cm.percentile, display:cm.display, mode:cm.mode},
    sess:(latest.r !== ceiling.r) ? {pct:lm.percentile, display:lm.display, mode:lm.mode} : null,
    raw:fmtRaw(ceiling.r, key), n:cm.sample_n,
  };
}

// SEAM-DOT (owner's choice): a miniature baseball. best-across = blue fill + coral seams; this-session
// = white fill + blue seams (reads as the secondary mark). Seam stroke bumped to 1.9 (24-unit viewBox)
// so the seams survive down to the smaller this-session size; a white ring gives a halo on the coral track.
// Two-COLOR seam dot: best-across = BLUE baseball, this-session = CORAL baseball; WHITE seams on both
// (white holds at size on any background). The body color is the best-vs-session distinction. A WHITE
// HALO RING keeps each dot visible on its OWN-color track end (blue-on-blue, coral-on-coral — proven the
// coral body blends there WITHOUT a ring). this-session uses a slightly DEEPER coral (#e3475f) so the body
// still reads as coral on the coral track end rather than dissolving into it.
const DOT_CORAL = "#e3475f";
function SeamDot({px, session}){
  var body = session ? DOT_CORAL : NAVY;
  return (
    <svg width={px} height={px} viewBox="0 0 24 24" style={{display:"block",filter:"drop-shadow(0 1px 1.3px rgba(0,0,0,.3))"}}>
      <circle cx="12" cy="12" r="11" fill={body} stroke="#ffffff" strokeWidth="1.3"/>
      <path d="M6.5 3.8 C9 7,9 17,6.5 20.2" fill="none" stroke="#ffffff" strokeWidth="1.9" strokeLinecap="round"/>
      <path d="M17.5 3.8 C15 7,15 17,17.5 20.2" fill="none" stroke="#ffffff" strokeWidth="1.9" strokeLinecap="round"/>
    </svg>
  );
}
function Mark({px, left, session, z}){
  return <div style={{position:"absolute",left:left+"%",top:"50%",transform:"translate(-50%,-50%)",zIndex:z}}><SeamDot px={px} session={session}/></div>;
}

// Estimated-tier marker (video): a HOLLOW DASHED DIAMOND — deliberately NOT the seam-dot baseball, so an
// estimate can NEVER be mistaken for a radar-Verified mark. Renders ONLY when a real band exists (see Bar).
function EstMark({px, left, z}){
  return <div style={{position:"absolute",left:left+"%",top:"50%",transform:"translate(-50%,-50%) rotate(45deg)",
    width:px,height:px,background:"rgba(138,147,163,.18)",border:"1.6px dashed "+EST,borderRadius:2,zIndex:z}}/>;
}

// one bar. best/sess = {pct, display, mode} or null. maturing band when best.mode==="range".
// est (video-velo framework) = {pct, value, band} or null/undefined — renders an Estimated mark + "Est. v
// ±band" ONLY when est.band is a real number. band null/absent OR no est => NOTHING (inert; no calibration
// yet). The estimate NEVER uses the seam-dot and never formats as a bare number. Additive: callers that
// don't pass est are unchanged.
// EXPORTED so Phase-E coach search renders results in the SAME two-color seam-dot grammar.
export function PercentilePeerBar({tool, best, sess, raw, n, note, est}){ return Bar({tool, best, sess, raw, n, note, est}); }
function Bar({tool, best, sess, raw, n, note, est}){
  var maturing = best && best.mode === "range";
  var overlay = [];
  if(maturing){
    var p = Number(best.pct), lo = Math.max(0, Math.floor(p/10)*10), hi = Math.min(100, lo+10);
    overlay.push(<div key="band" style={{position:"absolute",left:lo+"%",width:(hi-lo)+"%",top:0,bottom:0,background:"rgba(242,106,126,.34)",border:"1.4px solid "+CORAL,borderRadius:5,zIndex:1}}/>);
    overlay.push(<div key="bl" style={{position:"absolute",left:clampL((lo+hi)/2)+"%",top:-13,transform:"translateX(-50%)",font:"700 10.5px Inter,Arial",color:BLUEMID,whiteSpace:"nowrap",zIndex:3}}>{best.display}</div>);
  } else if(best){
    var bl = clampL(best.pct);
    overlay.push(<div key="bn" style={{position:"absolute",left:bl+"%",top:-14,transform:"translateX(-50%)",font:"800 11.5px Inter,Arial",color:NAVY,zIndex:4}}>{best.display}</div>);
    overlay.push(<Mark key="bm" px={15} left={bl} session={false} z={4}/>);
    if(sess){
      var sl = clampL(sess.pct);
      overlay.push(<Mark key="sm" px={12} left={sl} session={true} z={3}/>);
      overlay.push(<div key="sn" style={{position:"absolute",left:sl+"%",top:15,transform:"translateX(-50%)",font:"600 9px Inter,Arial",color:INK2,whiteSpace:"nowrap",zIndex:3}}>{sess.display}</div>);
    }
  }
  // ESTIMATED tier (video) — renders ONLY when an est reading carries a real band. band null/absent OR no
  // est => nothing (inert; there is no calibration yet). Distinct dashed diamond + "Est. v ±band" label,
  // never the seam-dot, never a bare number — so it can never read as radar-Verified.
  if(est && est.band != null && est.pct != null){
    var el = clampL(est.pct);
    overlay.push(<EstMark key="em" px={11} left={el} z={5}/>);
    overlay.push(<div key="en" style={{position:"absolute",left:el+"%",top:15,transform:"translateX(-50%)",font:"700 9px Inter,Arial",color:ESTLINE,whiteSpace:"nowrap",zIndex:5}}>{"Est. "+est.value+" ±"+est.band}</div>);
  }
  return (
    <div style={{display:"flex",alignItems:"center",gap:9,padding:"11px 0 9px"}}>
      <div style={{width:58,font:"700 12px Inter,Arial",color:INK,flex:"0 0 58px"}}>{tool}</div>
      <div style={{position:"relative",flex:1,height:13,margin:"6px 4px"}}>
        <div style={{position:"absolute",inset:0,borderRadius:7,background:TRACK_GRAD}}/>
        {overlay}
      </div>
      <div style={{flex:"0 0 98px",textAlign:"right",font:"700 11.5px Inter,Arial",color:INK,lineHeight:1.25}}>
        {raw || "—"}
        <div style={{font:"600 9.5px Inter,Arial",color:INK2}}>n={n}</div>
        {note && <div style={{font:"600 8.5px Inter,Arial",color:CORAL,marginTop:1}}>{note}</div>}
      </div>
    </div>
  );
}

function Legend(){
  return (
    <div style={{display:"flex",gap:14,alignItems:"center",margin:"2px 0 0 67px",font:"600 9px Inter,Arial",color:INK2,flex:"0 0 auto"}}>
      <span style={{display:"inline-flex",alignItems:"center",gap:4}}><SeamDot px={13}/> best across sessions</span>
      <span style={{display:"inline-flex",alignItems:"center",gap:4}}><SeamDot px={12} session={true}/> this session</span>
      <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:13,height:9,background:"rgba(242,106,126,.34)",border:"1.2px solid "+CORAL,borderRadius:3,display:"inline-block"}}/> sample maturing (range)</span>
    </div>
  );
}

// Build the bar rows + summary/detail/omissions, all DATA-DERIVED. The three radar states emerge from the
// data (no explicit flag): for each metric, a bar appears if it was measured THIS SESSION or in an approved
// PRIOR session. Two marks when both exist; single this-session mark when first-ever; single PRIOR mark +
// "no radar this session" note when this session didn't capture it but a prior did; NO bar when neither
// exists (the caller notes it in omissions). So: radar present -> two-mark · no-radar-but-prior -> prior-only
// bar · no-radar-ever -> bar omitted entirely (layout reflows to bar count, no hole).
function buildModel(r, priorReports){
  var pc = pctsOf(r); if(!pc) return null;
  var playerId = r.playerId || r.player_id;
  var bracket = pc.bracket || "";
  var rows = [], summary = [], detail = [];
  ORDER.forEach(function(key){
    var meta = META[key]; if(!meta) return;
    var neutral = meta.dir === "neutral";                            // distribution-position only (no "better")
    var m = pc.metrics[key];
    var thisSess = (m && m.percentile != null) ? {pct:m.percentile, display:m.display, mode:m.mode} : null;
    var best = bbotBestAcross(priorReports, playerId, key, r.id);   // approved-prior ceiling (or null)
    if(!thisSess && !best) return;                                   // no value anywhere -> no bar
    var hl, row;
    if(thisSess && best){                                            // two-mark
      row = {key:key, tool:meta.tool, best:{pct:best.percentile,display:best.display,mode:best.mode},
             sess:thisSess, raw:best.raw || fmtRaw(r,key), n:best.sample_n || m.sample_n};
      hl = best;
    } else if(thisSess){                                             // single this-session (first-ever)
      row = {key:key, tool:meta.tool, best:thisSess, sess:null, raw:fmtRaw(r,key), n:m.sample_n};
      hl = thisSess;
    } else {                                                         // PRIOR-ONLY (not captured this session)
      row = {key:key, tool:meta.tool, best:{pct:best.percentile,display:best.display,mode:best.mode},
             sess:null, raw:best.raw, n:best.sample_n, priorOnly:true,
             note:(meta.src === "radar" ? "no radar this session" : "not tracked this session")};
      hl = best;
    }
    // neutral tools render the bar as a DISTRIBUTION position; flag the row so it never reads as "better"
    if(neutral && !row.note) row.note = "distribution";
    rows.push(row);
    var pct = (hl.pct != null) ? hl.pct : hl.percentile;
    // headline restates measured-THIS-SESSION STRENGTHS only — neutral tools have no "better", so they
    // are excluded from the summary (a 90th-pct arm slot is not a strength claim).
    if(thisSess && !neutral) summary.push({tool:meta.tool, pct:pct, src:meta.src});
    detail.push({tool:meta.tool, raw:row.raw, pct:pct, bracket:bracket, priorOnly:!thisSess, neutral:neutral});
  });
  summary.sort(function(a,b){ return (b.pct||0)-(a.pct||0); });
  var omissions = r.omissions || (r.fullReport && r.fullReport.omissions) || [];
  return {rows:rows, summary:summary.slice(0,4), detail:detail, bracket:bracket, omissions:omissions};
}

export function BbotPercentileReport({r, sc, logo, priorReports}){
  var model = buildModel(r, priorReports);
  if(!model || !model.rows.length) return null; // dormant: nothing to show without percentile data
  var name = (sc && ((sc.firstName||"")+" "+(sc.lastName||"")).trim()) || r.playerName || "Player";
  var metaLine = [sc && sc.position, model.bracket, sc && (sc.gradYear ? ("Class of "+sc.gradYear) : null)].filter(Boolean).join(" · ");
  var film = r.videoUrl || r.video_url || r.reelUrl || (r.fullReport && r.fullReport.reelUrl) || "";
  var qr = film ? ("https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=0&color=2970b8&bgcolor=ffffff&data="+encodeURIComponent(film)) : "";

  return (
    <div style={{height:REPORT_H,display:"flex",flexDirection:"column",padding:"16px 18px 14px",fontFamily:"Inter,Arial,Helvetica,sans-serif",color:INK,background:"#fff"}}>
      {/* header */}
      <div style={{flex:"0 0 auto",display:"flex",justifyContent:"space-between",alignItems:"flex-start",borderBottom:"2px solid "+NAVY,paddingBottom:9}}>
        <div style={{display:"flex",gap:11,alignItems:"center"}}>
          {logo ? <img src={logo} alt="BBOT" style={{height:34,display:"block"}}/> : <div style={{font:"900 30px Inter,Arial",color:NAVY,letterSpacing:-1}}>BB<span style={{color:CORAL}}>{"●"}</span>T</div>}
          <div>
            <div style={{font:"800 19px Inter,Arial",color:INK,lineHeight:1.1}}>{name}</div>
            <div style={{font:"600 11px Inter,Arial",color:INK2,marginTop:2}}>{metaLine}</div>
          </div>
        </div>
        {qr && <div style={{textAlign:"center"}}>
          <img src={qr} alt="Scan to watch film" width={58} height={58} style={{display:"block"}} crossOrigin="anonymous"/>
          <div style={{font:"700 7.5px Inter,Arial",color:NAVY,marginTop:2,letterSpacing:.3}}>SCAN {"→"} WATCH FILM</div>
        </div>}
      </div>

      <div style={{flex:"0 0 auto",font:"600 10px Inter,Arial",color:INK2,margin:"7px 0 5px",fontStyle:"italic"}}>every mark measured or observed, nothing inferred</div>

      {/* SUMMARY — plain restatement of the backed percentiles below (no new claim) */}
      <div style={{flex:"0 0 auto",background:"#f3f7ff",border:"1px solid "+HAIR,borderRadius:9,padding:"9px 13px",marginBottom:4}}>
        <div style={{font:"800 10px Inter,Arial",color:INK,letterSpacing:.5,marginBottom:2}}>SUMMARY</div>
        <ul style={{margin:0,paddingLeft:16,font:"500 11px Inter,Arial",color:"#2b3a57",lineHeight:1.5}}>
          {model.summary.map(function(s,i){
            return <li key={i} style={{margin:"1px 0"}}>Stronger than ~{pctOfTen(s.pct)} of 10 peers his age — {s.tool} ({srcLabel(s.src)}).</li>;
          })}
        </ul>
      </div>

      {/* equal spacer (centers the bars+detail cluster so sparse reports stay balanced) */}
      <div style={{flex:"1 1 0",minHeight:6}}/>

      {/* PERCENTILE BAR BLOCK — the centerpiece. Reflows to whatever bars exist; no holes. */}
      <div style={{flex:"0 1 auto",display:"flex",flexDirection:"column",minHeight:0,margin:"3px 0 2px",border:"1px solid "+HAIR,borderRadius:10,padding:"8px 12px 10px",background:"#fbfcff"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:1,flex:"0 0 auto"}}>
          <div style={{font:"800 11px Inter,Arial",color:INK,letterSpacing:.4}}>PERCENTILE vs SAME-AGE PEERS</div>
          <div style={{font:"600 9px Inter,Arial",color:INK2}}>coral = stronger {"·"} left{"→"}right = 20th{"→"}99th</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",justifyContent:"space-evenly",minHeight:0,flex:"1 1 auto"}}>
          {model.rows.map(function(b){ return <Bar key={b.key} tool={b.tool} best={b.best} sess={b.sess} raw={b.raw} n={b.n} note={b.note}/>; })}
        </div>
        <Legend/>
      </div>

      {/* TOOL DETAIL — backed peer line per tool (Phase G enriches the phrasing) */}
      <div style={{flex:"0 1 auto",marginTop:8,overflow:"hidden"}}>
        <div style={{font:"800 10px Inter,Arial",color:INK,letterSpacing:.5,marginBottom:1}}>TOOL DETAIL</div>
        {model.detail.map(function(d,i){
          // neutral (no-"better") tools state a DISTRIBUTION position, never a "stronger than" claim.
          return <div key={i} style={{padding:"5px 0",borderTop:"1px solid "+HAIR}}>
            <span style={{font:"700 11px Inter,Arial",color:INK}}>{d.tool}.</span>{" "}
            {d.neutral
              ? <span style={{font:"400 10.5px Inter,Arial",color:"#33415c",lineHeight:1.5}}>{d.raw || "measured"}. At the {" "}
                  <span style={{color:INK,fontWeight:700}}>~{d.pct}th percentile of {d.bracket} peers</span> (distribution — where he falls, not a ranking){d.priorOnly ? ", best across sessions." : "."}</span>
              : <span style={{font:"400 10.5px Inter,Arial",color:"#33415c",lineHeight:1.5}}>{d.raw || "measured"}. Stronger than {" "}
                  <span style={{color:CORAL,fontWeight:700}}>~{d.pct}% of {d.bracket} peers</span>{d.priorOnly ? " (best across sessions — not captured this session)." : "."}</span>}
          </div>;
        })}
      </div>

      <div style={{flex:"1 1 0",minHeight:6}}/>

      {/* OMISSIONS — trust signal: what was NOT captured this session (never estimated) */}
      {model.omissions && model.omissions.length > 0 && (
        <div style={{flex:"0 0 auto",marginTop:6,background:"#fff5f6",border:"1px dashed "+CORAL,borderRadius:8,padding:"5px 11px"}}>
          <span style={{font:"700 9.5px Inter,Arial",color:CORAL}}>NOT MEASURED THIS SESSION</span>
          <span style={{font:"500 9.5px Inter,Arial",color:"#5a4a4f"}}> — {model.omissions.join("; ")} (never estimated).</span>
        </div>
      )}

      {/* FOOTER — the traveling, point-in-time peer-relative disclaimer */}
      <div style={{flex:"0 0 auto",marginTop:8,borderTop:"1px solid "+HAIR,paddingTop:7,font:"500 8.5px Inter,Arial",color:INK2,lineHeight:1.5}}>
        Percentiles are vs same-age peers (bracket {model.bracket}, reference n shown per tool), point-in-time — a snapshot of this evaluation, <b>not a guarantee or projection</b>. BBOT measures captured data and places it against peers; it does not predict outcomes. Sources: (radar) = device overlay, (measured) = pose/CV or on-screen timer.
        <span style={{float:"right",color:NAVY,fontWeight:700}}>Evaluator: BBOT</span>
      </div>
    </div>
  );
}

// ---------------- SAMPLE DATA + PREVIEW (dev-only; not used by the app) ----------------
function mkPct(metrics, bracket){ return {bracket:bracket, version:1, metrics:metrics}; }
export const SAMPLE_PITCHER = {
  id:9001, playerId:777, status:"approved", videoUrl:"https://media.643sports.com/r2/evals/gabriel-15u.mp4",
  measuredValues:{pitchVeloPeak:"86",pitchVeloAvg:"82"},
  percentiles: mkPct({
    pitchVeloPeak:{percentile:80,display:"80th",mode:"exact",sample_n:300},
    pitchVeloAvg:{percentile:69,display:"69th",mode:"exact",sample_n:300},
  }, "15U"),
  omissions:["Spin rate, pitch movement, command-to-location — no ball tracking this session"],
};
// RADAR-STATE proof samples (dev-only). 'prior' = no velo captured THIS session but prior approved velo
// exists -> velo shows prior-only mark. 'never' = no velo this session AND no prior -> velo bar omitted.
// device/timed tools measured this session (so the report isn't empty while velo shows prior-only/omitted)
const POSE_THIS_SESSION = {
  h2f:{percentile:66,display:"60-70th",mode:"range",sample_n:95},
  dash60:{percentile:78,display:"70-80th",mode:"range",sample_n:88},
};
const POSE_MV = {h2f:"4.3",dash60:"6.9"};
export const SAMPLE_PRIOR = {   // playerId 777 -> SAMPLE_PITCHER_PRIOR (also 777) supplies prior velo
  id:9002, playerId:777, status:"approved", videoUrl:"https://media.643sports.com/r2/evals/gabriel-15u.mp4",
  measuredValues:POSE_MV, percentiles: mkPct(POSE_THIS_SESSION, "15U"),
  omissions:["Spin rate, pitch movement, command-to-location — no ball tracking this session"],
};
export const SAMPLE_NEVER = {   // playerId 888 -> no prior matches -> no velo anywhere -> velo bar omitted
  id:9003, playerId:888, status:"approved", videoUrl:"https://media.643sports.com/r2/evals/player-15u.mp4",
  measuredValues:POSE_MV, percentiles: mkPct(POSE_THIS_SESSION, "15U"),
  omissions:["Fastball velocity — no radar this session and no prior on file","Spin rate, pitch movement, command-to-location — no ball tracking"],
};
// a prior approved session (his ceiling) so best-across shows the two-mark story
export const SAMPLE_PITCHER_PRIOR = {
  id:9000, playerId:777, status:"approved", date:"2026-03-01",
  measuredValues:{pitchVeloPeak:"86",pitchVeloAvg:"82"},
  percentiles: mkPct({
    pitchVeloPeak:{percentile:85,display:"85th",mode:"exact",sample_n:300},
    pitchVeloAvg:{percentile:72,display:"72nd",mode:"exact",sample_n:300},
  }, "15U"),
};
const SAMPLE_SC = {firstName:"Gabriel",lastName:"de Jesús",position:"RHP",gradYear:2028};

export function PercentilePreview(){
  return (
    <div style={{background:"#e9edf5",minHeight:"100vh",padding:20}}>
      <div style={{width:"8.5in",margin:"0 auto",background:"#fff",boxShadow:"0 2px 16px rgba(0,0,0,.15)"}}>
        <BbotPercentileReport r={SAMPLE_PITCHER} sc={SAMPLE_SC} logo={(typeof window!=="undefined"&&window.__BBOT_LOGO)||null} priorReports={[SAMPLE_PITCHER_PRIOR]}/>
      </div>
    </div>
  );
}

// ---------------- html2pdf ONE-PAGE HARNESS (dev-only) ----------------
// Runs the EXACT downloadReportPdf opts on a node that mimics what the production clone contains for a
// percentile report: the 760px-wide wrap (the legacy sections + card chrome are stripped in prod, so the
// clone is the report in a thin card border). Reports the REAL page count from the jsPDF instance so the
// REPORT_H height can be calibrated to exactly one page on the raster path.
function loadHtml2Pdf(){
  return new Promise(function(resolve, reject){
    if(window.html2pdf) return resolve(window.html2pdf);
    var s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
    s.onload = function(){ resolve(window.html2pdf); };
    s.onerror = reject;
    document.body.appendChild(s);
  });
}
// radar-state selector for the harness (?state=full|prior|never)
function radarStateCfg(){
  var s = (typeof window!=="undefined" && (window.location.search.match(/state=(\w+)/)||[])[1]) || "full";
  if(s === "prior") return {state:"prior", r:SAMPLE_PRIOR, pri:[SAMPLE_PITCHER_PRIOR]};
  if(s === "never") return {state:"never", r:SAMPLE_NEVER, pri:[]};
  return {state:"full", r:SAMPLE_PITCHER, pri:[SAMPLE_PITCHER_PRIOR]};
}
export function PercentilePdfTest(){
  var wrapRef = useRef(null);
  var cfg = radarStateCfg();
  var [result, setResult] = useState("running html2pdf…");
  useEffect(function(){
    var t = setTimeout(function(){
      loadHtml2Pdf().then(function(html2pdf){
        var src = wrapRef.current;
        if(!src) return;
        var opt = {                              // EXACT downloadReportPdf opts
          margin: [8,8,8,8],
          filename: "test.pdf",
          image: { type:"jpeg", quality:0.96 },
          html2canvas: { scale:2, useCORS:true, backgroundColor:"#ffffff" },
          jsPDF: { unit:"pt", format:"letter", orientation:"portrait" },
          pagebreak: { mode:["avoid-all","css","legacy"] }
        };
        html2pdf().set(opt).from(src).toPdf().get("pdf").then(function(pdf){
          var n = pdf.internal.getNumberOfPages();
          setResult("[" + cfg.state.toUpperCase() + "] PAGES=" + n + " | wrapHeight=" + src.offsetHeight + "px | REPORT_H=" + REPORT_H);
        }).catch(function(e){ setResult("ERROR: " + (e && e.message || e)); });
      }).catch(function(e){ setResult("LOAD ERROR: " + (e && e.message || e)); });
    }, 600);
    return function(){ clearTimeout(t); };
  }, []);
  return (
    <div style={{background:"#e9edf5",minHeight:"100vh",padding:20}}>
      <div id="pdfresult" style={{font:"800 22px Inter,Arial",color:"#fff",background:"#2970b8",padding:"12px 18px",borderRadius:8,marginBottom:16,textAlign:"center"}}>{result}</div>
      {/* mimic downloadReportPdf's wrap: white, padding 16, width 760; clone = thin card border + report */}
      <div ref={wrapRef} style={{background:"#fff",padding:16,width:760,margin:"0 auto"}}>
        <div style={{border:"1px solid #dbe6fb",borderRadius:12,overflow:"hidden"}}>
          <BbotPercentileReport r={cfg.r} sc={SAMPLE_SC} logo={(typeof window!=="undefined"&&window.__BBOT_LOGO)||null} priorReports={cfg.pri}/>
        </div>
      </div>
    </div>
  );
}
